import { NestedPlayer } from "@/types/serializers"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios, { AxiosError } from "axios"
import toast from "react-hot-toast"
import { captureEvent } from "@/lib/posthog"

const usePlayers = () => {
  const queryClient = useQueryClient()

  const {
    mutate: createPlayer,
    mutateAsync: createPlayerAsync,
    isPending,
  } = useMutation({
    mutationFn: async (data: { name: string; discoverable_player_id?: string }) => {
      const { data: player } = await axios.post("/api/players", data)
      return player
    },
    onSuccess: () => {
      captureEvent("player_created")
      queryClient.invalidateQueries({
        queryKey: ["players"],
      })
    },
    onError: (error: AxiosError<{ error?: string }>) =>
      toast.error(error.response?.data?.error || "Failed to create player"),
  })

  const {
    data: rawPlayers,
    isLoading,
    error,
  } = useQuery({
    queryFn: async (): Promise<NestedPlayer[]> => {
      const res = await axios.get("/api/players")
      return Array.isArray(res.data) ? res.data : []
    },
    queryKey: ["players"],
    staleTime: 1000 * 60 * 5,
  })
  const players = Array.isArray(rawPlayers) ? rawPlayers : []

  return {
    createPlayer,
    createPlayerAsync,
    isPending,
    players,
    isLoading,
    error,
  }
}

export default usePlayers
